import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const STORAGE_KEY = '2fa_settings';

const generateBackupCodes = () => {
  const codes = [];
  for (let i = 0; i < 8; i++) {
    const part1 = Math.random()?.toString(36)?.substring(2, 7)?.toUpperCase();
    const part2 = Math.random()?.toString(36)?.substring(2, 7)?.toUpperCase();
    codes?.push(`${part1}-${part2}`);
  }
  return codes;
};

const generateSmsCode = () => {
  return Math.floor(100000 + Math.random() * 900000)?.toString();
};

const TwoFactorAuth = () => {
  const [settings, setSettings] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {
        enabled: false,
        phoneNumber: '',
        backupCodes: [],
        setupComplete: false
      };
    } catch {
      return { enabled: false, phoneNumber: '', backupCodes: [], setupComplete: false };
    }
  });

  const [step, setStep] = useState('idle'); // idle | phone | verify | backup | done
  const [phoneInput, setPhoneInput] = useState(settings?.phoneNumber || '');
  const [smsCode, setSmsCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [codeInputs, setCodeInputs] = useState(['', '', '', '', '', '']);
  const [smsSent, setSmsSent] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [disableConfirm, setDisableConfirm] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);
  const cooldownRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {}
  }, [settings]);

  useEffect(() => {
    return () => { if (cooldownRef?.current) clearInterval(cooldownRef?.current); };
  }, []);

  const startCooldown = () => {
    setResendCooldown(30);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef?.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleEnableStart = () => {
    setStep('phone');
    setPhoneInput(settings?.phoneNumber || '');
    setSmsError('');
    setVerifyError('');
    setSuccessMsg('');
  };

  const handleSendSms = () => {
    const cleaned = phoneInput?.replace(/\s/g, '');
    if (!cleaned || cleaned?.length < 8) {
      setSmsError('Vennligst oppgi et gyldig telefonnummer.');
      return;
    }
    setSmsError('');
    const code = generateSmsCode();
    setGeneratedCode(code);
    setCodeInputs(['', '', '', '', '', '']);
    setSmsSent(true);
    setStep('verify');
    startCooldown();
    setSuccessMsg(`SMS-kode sendt til ${cleaned} (simulert)`);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleCodeInput = (index, value) => {
    if (!/^\d?$/?.test(value)) return;
    const updated = [...codeInputs];
    updated[index] = value;
    setCodeInputs(updated);
    setVerifyError('');
    if (value && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !codeInputs?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handleVerify = () => {
    const entered = codeInputs?.join('');
    if (entered?.length < 6) {
      setVerifyError('Skriv inn alle 6 sifrene.');
      return;
    }
    if (entered !== generatedCode) {
      setVerifyError('Feil kode. Prøv igjen.');
      setCodeInputs(['', '', '', '', '', '']);
      inputRefs?.current?.[0]?.focus();
      return;
    }
    setVerifyError('');
    const codes = generateBackupCodes();
    setBackupCodes(codes);
    setStep('backup');
  };

  const handleResend = () => {
    if (resendCooldown > 0) return;
    const code = generateSmsCode();
    setGeneratedCode(code);
    setCodeInputs(['', '', '', '', '', '']);
    setVerifyError('');
    startCooldown();
    setSuccessMsg(`Ny SMS-kode sendt til ${phoneInput?.replace(/\s/g, '')} (simulert)`);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleFinishSetup = () => {
    const newSettings = {
      enabled: true,
      phoneNumber: phoneInput?.replace(/\s/g, ''),
      backupCodes,
      setupComplete: true
    };
    setSettings(newSettings);
    setStep('done');
    setSuccessMsg('To-faktor autentisering er aktivert!');
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  const handleDisable = () => {
    if (!disableConfirm) {
      setDisableConfirm(true);
      return;
    }
    setSettings({ enabled: false, phoneNumber: '', backupCodes: [], setupComplete: false });
    setStep('idle');
    setDisableConfirm(false);
    setSuccessMsg('To-faktor autentisering er deaktivert.');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleDownloadCodes = () => {
    const codesToDownload = settings?.backupCodes?.length > 0 ? settings?.backupCodes : backupCodes;
    const content = [
      'VEIASSISTENT - SIKKERHETSKODER FOR TO-FAKTOR AUTENTISERING',
      '='?.repeat(55),
      '',
      'Disse kodene kan brukes én gang hver for å logge inn',
      'dersom du ikke har tilgang til telefonen din.',
      '',
      'Oppbevar disse kodene på et trygt sted.',
      '',
      ...codesToDownload?.map((code, i) => `${i + 1}. ${code}`),
      '',
      `Generert: ${new Date()?.toLocaleString('nb-NO')}`,
      'Konto: Veiassistent'
    ]?.join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'veiassistent-sikkerhetskoder.txt';
    a?.click();
    URL.revokeObjectURL(url);
  };

  const handleCancel = () => {
    setStep('idle');
    setSmsError('');
    setVerifyError('');
    setSmsSent(false);
    setDisableConfirm(false);
    setCodeInputs(['', '', '', '', '', '']);
  };

  const maskedPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone?.replace(/\s/g, '');
    if (cleaned?.length < 4) return cleaned;
    return cleaned?.slice(0, -4)?.replace(/./g, '•') + cleaned?.slice(-4);
  };

  return (
    <div className="bg-card border-2 border-border rounded-[var(--radius-md)] p-6 md:p-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-4">
          <div
            className="flex-shrink-0 w-12 h-12 rounded-[var(--radius-sm)] flex items-center justify-center"
            style={{ background: 'rgba(255, 87, 34, 0.12)' }}
          >
            <Icon name="Smartphone" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="text-xl md:text-2xl font-semibold text-foreground font-heading">
              To-faktor autentisering (2FA)
            </h3>
            <p className="text-sm text-muted-foreground font-body mt-1">
              Beskytt kontoen din med SMS-bekreftelse ved innlogging.
            </p>
          </div>
        </div>
        {/* Status badge */}
        <div
          className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-xs font-semibold font-caption"
          style={{
            borderColor: settings?.enabled ? 'var(--color-success)' : 'rgba(156,163,175,0.3)',
            color: settings?.enabled ? 'var(--color-success)' : 'var(--color-muted-foreground)',
            background: settings?.enabled ? 'rgba(16,185,129,0.08)' : 'transparent'
          }}
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: settings?.enabled ? 'var(--color-success)' : 'var(--color-muted-foreground)' }}
          />
          {settings?.enabled ? 'Aktivert' : 'Deaktivert'}
        </div>
      </div>
      {/* Success message */}
      {successMsg && (
        <div
          className="flex items-center gap-3 p-4 rounded-[var(--radius-sm)] mb-6 border-2"
          style={{ background: 'rgba(16,185,129,0.08)', borderColor: 'rgba(16,185,129,0.3)' }}
        >
          <Icon name="CheckCircle" size={18} color="var(--color-success)" />
          <span className="text-sm font-medium text-foreground font-body">{successMsg}</span>
        </div>
      )}
      {/* IDLE STATE - 2FA disabled */}
      {step === 'idle' && !settings?.enabled && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: 'Lock', title: 'Ekstra sikkerhet', desc: 'Krev SMS-kode ved innlogging' },
              { icon: 'Shield', title: 'Kontobeskytt', desc: 'Stopp uautorisert tilgang' },
              { icon: 'Key', title: 'Sikkerhetskoder', desc: '8 engangskoder som backup' }
            ]?.map((item, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center p-4 rounded-[var(--radius-sm)] border-2"
                style={{ borderColor: 'rgba(255,87,34,0.2)', background: 'rgba(255,87,34,0.04)' }}
              >
                <div
                  className="w-10 h-10 rounded-[var(--radius-sm)] flex items-center justify-center mb-3"
                  style={{ background: 'rgba(255,87,34,0.12)' }}
                >
                  <Icon name={item?.icon} size={20} color="var(--color-accent)" />
                </div>
                <p className="text-sm font-semibold text-foreground font-heading mb-1">{item?.title}</p>
                <p className="text-xs text-muted-foreground font-caption">{item?.desc}</p>
              </div>
            ))}
          </div>
          <Button
            variant="default"
            iconName="Shield"
            iconPosition="left"
            onClick={handleEnableStart}
            className="w-full sm:w-auto"
            style={{ background: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}
          >
            Aktiver to-faktor autentisering
          </Button>
        </div>
      )}
      {/* IDLE STATE - 2FA enabled */}
      {step === 'idle' && settings?.enabled && (
        <div className="space-y-6">
          <div
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-[var(--radius-sm)] border-2"
            style={{ borderColor: 'rgba(16,185,129,0.25)', background: 'rgba(16,185,129,0.06)' }}
          >
            <div className="flex items-center gap-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              <div>
                <p className="text-sm font-semibold text-foreground font-heading">2FA er aktivert</p>
                <p className="text-xs text-muted-foreground font-caption">
                  Telefon: {maskedPhone(settings?.phoneNumber)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={handleDownloadCodes}
                className="text-xs"
              >
                Last ned koder
              </Button>
            </div>
          </div>

          {/* Backup codes preview */}
          {settings?.backupCodes?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-foreground font-heading mb-3 flex items-center gap-2">
                <Icon name="Key" size={16} color="var(--color-accent)" />
                Sikkerhetskoder ({settings?.backupCodes?.length} tilgjengelig)
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {settings?.backupCodes?.map((code, i) => (
                  <div
                    key={i}
                    className="px-3 py-2 rounded-[var(--radius-sm)] border-2 text-center"
                    style={{ borderColor: 'rgba(255,87,34,0.2)', background: 'rgba(255,87,34,0.04)' }}
                  >
                    <span className="text-xs font-mono font-semibold text-foreground tracking-wider">{code}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Disable section */}
          <div
            className="p-4 rounded-[var(--radius-sm)] border-2"
            style={{ borderColor: 'rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.04)' }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-foreground font-heading">Deaktiver 2FA</p>
                <p className="text-xs text-muted-foreground font-caption">
                  {disableConfirm ? 'Er du sikker? Klikk igjen for å bekrefte.' : 'Dette vil fjerne SMS-beskyttelse fra kontoen din.'}
                </p>
              </div>
              <div className="flex gap-2">
                {disableConfirm && (
                  <Button variant="outline" onClick={() => setDisableConfirm(false)} className="text-xs">
                    Avbryt
                  </Button>
                )}
                <button
                  onClick={handleDisable}
                  className="px-4 py-2 rounded-[var(--radius-sm)] border-2 text-xs font-semibold transition-all"
                  style={{
                    borderColor: disableConfirm ? 'var(--color-error)' : 'rgba(239,68,68,0.4)',
                    color: 'var(--color-error)',
                    background: disableConfirm ? 'rgba(239,68,68,0.12)' : 'transparent'
                  }}
                >
                  {disableConfirm ? 'Bekreft deaktivering' : 'Deaktiver'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* STEP: Phone number input */}
      {step === 'phone' && (
        <div className="space-y-6">
          <div
            className="p-4 rounded-[var(--radius-sm)] border-2"
            style={{ borderColor: 'rgba(255,87,34,0.25)', background: 'rgba(255,87,34,0.06)' }}
          >
            <p className="text-sm text-foreground font-body">
              <span className="font-semibold" style={{ color: 'var(--color-accent)' }}>Steg 1 av 3:</span>{' '}
              Oppgi telefonnummeret ditt for å motta SMS-koder.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground font-heading mb-2">
              Telefonnummer
            </label>
            <div className="flex gap-3">
              <div
                className="flex items-center px-3 rounded-[var(--radius-sm)] border-2 text-sm font-medium text-muted-foreground font-body flex-shrink-0"
                style={{ borderColor: 'var(--color-border)', background: 'var(--color-surface-elevated)' }}
              >
                🇳🇴 +47
              </div>
              <input
                type="tel"
                value={phoneInput}
                onChange={(e) => { setPhoneInput(e?.target?.value); setSmsError(''); }}
                placeholder="900 00 000"
                className="flex-1 px-4 py-3 rounded-[var(--radius-sm)] border-2 bg-surface-elevated text-foreground font-body text-sm outline-none transition-all"
                style={{
                  borderColor: smsError ? 'var(--color-error)' : 'var(--color-border)',
                  background: 'var(--color-surface-elevated)'
                }}
                onFocus={(e) => { if (!smsError) e.target.style.borderColor = 'var(--color-accent)'; }}
                onBlur={(e) => { if (!smsError) e.target.style.borderColor = 'var(--color-border)'; }}
                onKeyDown={(e) => e?.key === 'Enter' && handleSendSms()}
              />
            </div>
            {smsError && (
              <p className="text-xs mt-2 flex items-center gap-1" style={{ color: 'var(--color-error)' }}>
                <Icon name="AlertCircle" size={12} color="var(--color-error)" />
                {smsError}
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={handleCancel}>Avbryt</Button>
            <button
              onClick={handleSendSms}
              className="flex items-center gap-2 px-6 py-2.5 rounded-[var(--radius-sm)] text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: 'var(--color-accent)' }}
            >
              <Icon name="Send" size={16} color="white" />
              Send SMS-kode
            </button>
          </div>
        </div>
      )}
      {/* STEP: SMS verification */}
      {step === 'verify' && (
        <div className="space-y-6">
          <div
            className="p-4 rounded-[var(--radius-sm)] border-2"
            style={{ borderColor: 'rgba(255,87,34,0.25)', background: 'rgba(255,87,34,0.06)' }}
          >
            <p className="text-sm text-foreground font-body">
              <span className="font-semibold" style={{ color: 'var(--color-accent)' }}>Steg 2 av 3:</span>{' '}
              Skriv inn 6-sifret kode sendt til{' '}
              <span className="font-semibold">{phoneInput}</span> (simulert).
            </p>
          </div>

          {/* 6-digit code inputs */}
          <div>
            <label className="block text-sm font-semibold text-foreground font-heading mb-3">
              Bekreftelseskode
            </label>
            <div className="flex gap-2 sm:gap-3">
              {codeInputs?.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeInput(i, e?.target?.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg font-bold font-data rounded-[var(--radius-sm)] border-2 outline-none transition-all"
                  style={{
                    borderColor: verifyError ? 'var(--color-error)' : digit ? 'var(--color-accent)' : 'var(--color-border)',
                    background: 'var(--color-surface-elevated)',
                    color: 'var(--color-foreground)'
                  }}
                />
              ))}
            </div>
            {verifyError && (
              <p className="text-xs mt-2 flex items-center gap-1" style={{ color: 'var(--color-error)' }}>
                <Icon name="AlertCircle" size={12} color="var(--color-error)" />
                {verifyError}
              </p>
            )}
          </div>

          {/* Dev hint */}
          <div
            className="flex items-center gap-2 p-3 rounded-[var(--radius-sm)] border"
            style={{ borderColor: 'rgba(156,163,175,0.2)', background: 'rgba(156,163,175,0.05)' }}
          >
            <Icon name="Info" size={14} color="var(--color-muted-foreground)" />
            <span className="text-xs text-muted-foreground font-caption">
              Demo-modus: Bruk koden <span className="font-mono font-bold text-foreground">{generatedCode}</span>
            </span>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={handleCancel}>Avbryt</Button>
            <button
              onClick={handleVerify}
              className="flex items-center gap-2 px-6 py-2.5 rounded-[var(--radius-sm)] text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: 'var(--color-accent)' }}
            >
              <Icon name="CheckCircle" size={16} color="white" />
              Bekreft kode
            </button>
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="flex items-center gap-2 px-4 py-2.5 rounded-[var(--radius-sm)] text-sm font-medium border-2 transition-all"
              style={{
                borderColor: resendCooldown > 0 ? 'rgba(156,163,175,0.2)' : 'var(--color-border)',
                color: resendCooldown > 0 ? 'var(--color-muted-foreground)' : 'var(--color-foreground)',
                opacity: resendCooldown > 0 ? 0.6 : 1
              }}
            >
              <Icon name="RefreshCw" size={14} color={resendCooldown > 0 ? 'var(--color-muted-foreground)' : 'var(--color-foreground)'} />
              {resendCooldown > 0 ? `Send på nytt (${resendCooldown}s)` : 'Send på nytt'}
            </button>
          </div>
        </div>
      )}
      {/* STEP: Backup codes */}
      {step === 'backup' && (
        <div className="space-y-6">
          <div
            className="p-4 rounded-[var(--radius-sm)] border-2"
            style={{ borderColor: 'rgba(255,87,34,0.25)', background: 'rgba(255,87,34,0.06)' }}
          >
            <p className="text-sm text-foreground font-body">
              <span className="font-semibold" style={{ color: 'var(--color-accent)' }}>Steg 3 av 3:</span>{' '}
              Lagre sikkerhetskodene dine. Disse kan brukes én gang hver dersom du mister tilgang til telefonen.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-foreground font-heading flex items-center gap-2">
                <Icon name="Key" size={16} color="var(--color-accent)" />
                Dine sikkerhetskoder
              </p>
              <button
                onClick={handleDownloadCodes}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--radius-sm)] border-2 text-xs font-semibold transition-all hover:opacity-80"
                style={{ borderColor: 'var(--color-accent)', color: 'var(--color-accent)', background: 'rgba(255,87,34,0.08)' }}
              >
                <Icon name="Download" size={12} color="var(--color-accent)" />
                Last ned .txt
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {backupCodes?.map((code, i) => (
                <div
                  key={i}
                  className="px-3 py-2.5 rounded-[var(--radius-sm)] border-2 text-center"
                  style={{ borderColor: 'rgba(255,87,34,0.25)', background: 'rgba(255,87,34,0.06)' }}
                >
                  <span className="text-xs font-mono font-bold tracking-wider" style={{ color: 'var(--color-accent)' }}>
                    {code}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div
            className="flex items-start gap-3 p-4 rounded-[var(--radius-sm)] border-2"
            style={{ borderColor: 'rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.06)' }}
          >
            <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
            <p className="text-xs text-muted-foreground font-caption">
              <span className="font-semibold text-foreground">Viktig:</span> Oppbevar disse kodene på et trygt sted utenfor enheten din. Hver kode kan kun brukes én gang.
            </p>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={handleCancel}>Avbryt</Button>
            <button
              onClick={handleFinishSetup}
              className="flex items-center gap-2 px-6 py-2.5 rounded-[var(--radius-sm)] text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: 'var(--color-accent)' }}
            >
              <Icon name="ShieldCheck" size={16} color="white" />
              Aktiver 2FA
            </button>
          </div>
        </div>
      )}
      {/* STEP: Done */}
      {step === 'done' && (
        <div className="space-y-4">
          <div
            className="flex flex-col items-center text-center p-8 rounded-[var(--radius-sm)] border-2"
            style={{ borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.06)' }}
          >
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background: 'rgba(16,185,129,0.15)' }}
            >
              <Icon name="ShieldCheck" size={32} color="var(--color-success)" />
            </div>
            <h4 className="text-lg font-bold text-foreground font-heading mb-2">2FA er aktivert!</h4>
            <p className="text-sm text-muted-foreground font-body max-w-sm">
              Kontoen din er nå beskyttet med to-faktor autentisering. Du vil motta en SMS-kode ved neste innlogging.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setStep('idle')}
            iconName="ArrowLeft"
            iconPosition="left"
          >
            Tilbake til innstillinger
          </Button>
        </div>
      )}
    </div>
  );
};

export default TwoFactorAuth;
